import { onMounted, onUnmounted, ref, watch, type Ref } from "vue";

type BackButtonDetectorOptions = {
  onBackButtonClick: () => void;
};

const GUARD_STATE = { guard: true, reason: "back-button-detection" };

export function useUserBackButtonClickDetector({ onBackButtonClick }: BackButtonDetectorOptions) {
  const isAlreadyDetected: Ref<boolean> = ref(false);
  const isDetectionStartedRef: Ref<boolean> = ref(false);
  const onBackButtonClickRef: Ref<() => void> = ref(onBackButtonClick);
  const startMonitoring: Ref<boolean> = ref(false);

  const pushGuardState = () => {
    try {
      // history.length === 1 means there is nothing to go back for the user
      if (window.history.length > 1) {
        window.history.pushState(GUARD_STATE, "", window.location.href);
      }
    } catch (err) {
      // Silently fail if pushState is not available
    }
  };

  watch(
    () => onBackButtonClick,
    newCallback => {
      onBackButtonClickRef.value = newCallback;
    }
  );

  const handlePopState = () => {
    if (!isAlreadyDetected.value) {
      onBackButtonClickRef.value?.();
      isAlreadyDetected.value = true;
    }
  };

  const startDetection = () => {
    if (isDetectionStartedRef.value) return;

    isDetectionStartedRef.value = true;

    pushGuardState();

    window.addEventListener("popstate", handlePopState);
  };

  onMounted(() => {
    if (startMonitoring.value) {
      startDetection();
    }
  });

  onUnmounted(() => {
    isDetectionStartedRef.value = false;
    window.removeEventListener("popstate", handlePopState);
  });

  watch(
    () => startMonitoring.value,
    shouldMonitor => {
      if (shouldMonitor && !isDetectionStartedRef.value) {
        startDetection();
      }
    },
    { immediate: false }
  );

  return {
    triggerUserBackButtonClickMonitoring: () => {
      startMonitoring.value = true;
    }
  };
}
